import * as React from 'react';

import { Result } from 'core/infrastructure';
import { Res } from 'core/api';
import { Environment } from 'core/utils';

import * as _Form from 'core/ui/controls/Form';

import { Message, Modal } from '../modal';
import { DataGridColumn } from 'core/ui/controls/DataGrid.Column';
import { ColumnFilter, createFilters, getPossibleArguments } from 'core/ui/controls/DataGrid.Filter';
import { Box, DialogBody, DialogFooter, DialogHeader, DialogTitle, HStack } from '@chakra-ui/react';
import { createFilterControls } from 'core/ui/controls/DataGrid.FilterControl';
import LegacyButton from 'core/ui/controls/Button';
import { BasicButton } from '../BasicButton';

type Props<T> = {
	columns: DataGridColumn<T>[];
	currentFilters: ColumnFilter<T>[];
	allPossibleFilters: ColumnFilter<T>[];
	items: T[];
} & Modal.Actions<ColumnFilter<T>[]>;

type State<T> = {
	filters: ColumnFilter<T>[];
};

class FilterDialog<T> extends React.Component<Props<T>, State<T>> {

	private form = React.createRef<_Form.Form<State<T>>>();
	private bodyRef = React.createRef<HTMLDivElement>();
	private possibleArguments: { [propertyPath: string]: string[] };


	// Creo esta función scrollBodyToBottom, para controlar que cuando se añade un nuevo filtro,
	// el scroll quede abajo del todo y no se oculten los botones de añadir mas filtros o eliminar todos
	// recupero la referencia del Body Dialog ajusto el scroll y con requestAnimationFrame aseguro que el callback lo renderize en el setState.
	private scrollBodyToBottom() {
		const el = this.bodyRef.current;
		if (!el)
			return;
		el.scrollTop = el.scrollHeight;
	}

	private deferScrollBodyToBottom() {
		requestAnimationFrame(() => requestAnimationFrame(() => this.scrollBodyToBottom()));
	}

	constructor(props: Props<T>) {
		super(props);

		this.onKeyDown = this.onKeyDown.bind(this);
		this.onOK = this.onOK.bind(this);
		this.onCancel = this.onCancel.bind(this);
		this.onAddFilter = this.onAddFilter.bind(this);
		this.onRemoveFilter = this.onRemoveFilter.bind(this);
		this.onRemoveAllFilters = this.onRemoveAllFilters.bind(this);

		this.possibleArguments = {};

		this.state = {
			// Clono los filtros actuales para no romper nada
			filters: props.currentFilters.map(filter => ({ ...filter }))
		};
	}

	private getCachedPossibleArguments(filter: ColumnFilter<T>): string[] {
		if (this.possibleArguments[filter.propertyPath])
			return this.possibleArguments[filter.propertyPath];

		return this.possibleArguments[filter.propertyPath] = getPossibleArguments(filter, this.props.items);
	}

	private onAddFilter() {
		this.setState({
			filters: [...this.state.filters, { ...this.props.allPossibleFilters[0], argument: '' }]
		}, () => this.deferScrollBodyToBottom());
	}

	private async onRemoveAllFilters() {
		this.setState({
			filters: []
		}, () => this.form.current!.remountAll());
	}

	private async onRemoveFilter(filter: ColumnFilter<T>) {
		await this.form.current!.updateModel();
		this.setState({
			filters: this.state.filters.filter(x => x !== filter)
		}, () => this.form.current!.remountAll());
	}

	private async onOK() {
		await this.form.current!.ifValid().then(() => this.props.close(this.state.filters));
	}

	private onCancel() {
		this.props.cancel();
	}

	private async onKeyDown(e: React.KeyboardEvent<any>) {

		// Si no lo hacemos nos lanza el siguiente error: 
		// This synthetic event is reused for performance reasons.If you're seeing this, you're accessing the method`keyCode` on a released / nullified synthetic event.This is a no - op function.If you must keep the original synthetic event around, use event.persist()
		e.persist();

		// Tengo unos fantásticos métodos de ayuda para hacer esto en UI, pero no puedo usarlos
		// porque introduciría una dependencia circular

		// En el caso de android, cuando el type no es text, en lugar de un botón de Enter (Ir)
		// muestra un botón de Siguiente (Sig) equivalente al tab, lo que imposibilita al usuario
		// pulsar Enter. Para ñapearlo, si es android (y sólo si es android) equiparo el tab al
		// Enter
		if (e.keyCode === 13 || (Environment.isAndroid() && e.keyCode === 9)) {
			e.preventDefault();
			e.stopPropagation();
			await this.onOK();
		}

		if (e.keyCode === 27) {
			e.preventDefault();
			e.stopPropagation();
			this.onCancel();
		}
	}

	render() {
		const Form: _Form.FormCtor<State<T>> = _Form.Form;

		return <Box width={'full'} bg={{ base: 'white', _dark: '{colors.neutral.900}' }} onKeyDown={this.onKeyDown}>
			<DialogHeader p={'2.5'} borderBottom={'1px solid'} borderColor={{ base: '{colors.neutral.200}', _dark: '{colors.neutral.700}' }}>
				<DialogTitle>{Res.WA.WA_DATAGRID_FILTER()}</DialogTitle>
			</DialogHeader>
			<DialogBody ref={this.bodyRef} className='legacy-styles' paddingY={'2.5'} paddingX={'4'} maxHeight={'60'} overflow={'auto'} scrollBehavior={'smooth'}>
				<Form model={this.state} ref={this.form}>
					{this.state.filters.map((filter, idx) => createFilterControls({
						pathPrefix: `filters[${idx}]`,
						filter,
						allPossibleFilters: this.props.allPossibleFilters,
						possibleArguments: this.getCachedPossibleArguments(filter),
						columns: this.props.columns,
						onChange: async () => {
							await this.form.current!.remount(`filters[${idx}].propertyPath`, `filters[${idx}].operator`, `filters[${idx}].argument`);
							this.forceUpdate(() => this.deferScrollBodyToBottom());
						},
						onRemove: () => this.onRemoveFilter(filter)
					}))}
				</Form>

				{this.state.filters.length ? null : <h3 className='font-trans m-b-lg'>{Res.WA.WA_DATAGRID_FILTER_NO_FILTER()}</h3>}

				<HStack justifyContent={'space-between'}>
					<LegacyButton onClick={this.onAddFilter}><i className='text-info fa fa-plus'></i> {Res.Common.ADD_BTN()}</LegacyButton>
					<LegacyButton onClick={this.onRemoveAllFilters}><i className='text-danger fa fa-minus'></i> {Res.WA.WA_DATAGRID_FILTER_REMOVE_ALL()}</LegacyButton>
				</HStack>

			</DialogBody>
			<DialogFooter borderTop={'1px solid'} borderColor={{ base: '{colors.neutral.200}', _dark: '{colors.neutral.700}' }} padding={'2.5'}>
				{/* Meto este paddingTop para que quede visible el comboBox, es una ñapa y, el día que rehagamos esta vista va a desaparecer */}
				<HStack width={'full'} justifyContent={'flex-end'} paddingTop={'32'}>
					<BasicButton type='primary' width={'18'} text={Res.Common.OK_BTN()} onClick={this.onOK} />
					<BasicButton width={'18'} text={Res.Common.CANCEL_BTN()} onClick={this.onCancel} />
				</HStack>
			</DialogFooter>
		</Box >;
	}
}

export async function configureFilter<T>(currentFilters: ColumnFilter<T>[], columns: DataGridColumn<T>[], items: T[]): Promise<Result<ColumnFilter<T>[]>> {
	const allPossibleFilters = columns.flatMap(column => createFilters(column, items));

	if (allPossibleFilters.length === 0) {
		await Message.show(Res.Msg.MSG_CANNOT_APPLY_FILTER_WITH_CURRENT_DATA());
		return Result.error();
	}

	return Modal.show(FilterDialog<T>, { currentFilters, columns, items, allPossibleFilters, placement: 'top', size: 'xl' });
}
